import 'edcom-ui/global.less';
import 'edcom-ui/styles/flexbox.less';
import express from 'express'; // Used only for typings
import React from 'react';
import clsx from 'clsx';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import StandardLayout from '../components/layouts/StandardLayout';
import RequestContext, {
  getRequestDataFromRequest,
  RequestData,
} from '../services/RequestContext';
import { AccountProvider } from '../services/AccountContext';
import { GDPRProvider } from '../services/GDPRContext';
import { NotificationProvider } from '../services/NotificationContext';
import { PlayersProvider } from '../services/PlayersContext';
import getConfig from 'next/config';
import '../edcom-ui/styles/flatpickr.less';
import content from '../services/content';

type WrappedRequestData = {
  requestData: RequestData;
};

// NextJS incorrectly types the AppContext.ctx.req as http.IncomingRequest instead of express.Request
type WrappedAppContext = AppContext & {
  ctx: {
    req: express.Request;
  };
};

// Show debug info on non-prod environments
const {
  publicRuntimeConfig: { APP_ENV },
} = getConfig();
const EnvironmentInfo = dynamic(
  () =>
    Promise.resolve(
      APP_ENV !== 'prod' ? import('../components/EnvironmentInfo') : null
    ),
  { ssr: false }
);

function WrappedApp(props: AppProps & WrappedRequestData): React.ReactElement {
  const {
    Component,
    requestData,
    pageProps: { pageKey, navProps, footerProps, ...pageProps },
  } = props;

  const [a11y, setA11y] = React.useState(false);

  const handleMouseDown: React.MouseEventHandler = (event): void => {
    setA11y(false);
  };

  const handleKeyDown: React.KeyboardEventHandler = () => {
    setA11y(true);
  };

  // Important! Only render the error page in the event that one of our components was the source
  // of the error to prevent infinite rendering loops
  if (Component.displayName === 'Error') {
    return <Component {...pageProps} />;
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      className={clsx({ 'a11y-disable': !a11y })}
    >
      <RequestContext.Provider value={requestData}>
        <GDPRProvider>
          <AccountProvider>
            <NotificationProvider>
              <PlayersProvider>
                <StandardLayout
                  pageKey={pageKey}
                  navProps={navProps}
                  footerProps={footerProps}
                >
                  <Component {...pageProps} />
                </StandardLayout>
                <EnvironmentInfo />
              </PlayersProvider>
            </NotificationProvider>
          </AccountProvider>
        </GDPRProvider>
      </RequestContext.Provider>
    </div>
  );
}

WrappedApp.getInitialProps = async (
  appContext: WrappedAppContext
): Promise<AppInitialProps & WrappedRequestData> => {
  const req = appContext?.ctx?.req;
  const requestData = getRequestDataFromRequest(req);

  const appProps = await App.getInitialProps(appContext);
  const navInfo = await content(req).getNavigationInfo();

  // Add the navigation info to navProps
  if (appProps.pageProps) {
    appProps.pageProps.navProps = { ...appProps.pageProps?.navProps, navInfo };
  }

  return { ...appProps, requestData };
};

export default WrappedApp;
